import { YStack } from "../Stacks";
import { useGlassProminence } from "./GlassScope";
import { GlassTheme } from "./GlassTheme";
import type { GlassSurfaceProps } from "./types";

// Theme values arrive as CSS variables, so light and dark glass share one
// stylesheet. @supports, prefers-contrast and prefers-reduced-transparency need
// real CSS, which inline styles can't express.
const glassCss = `
.glass {
  background-color: var(--glassSolid);
  box-shadow: inset 0 1px 0 var(--glassHighlight), inset 0 0 0 1px var(--glassRim), var(--glassShadow);
}
.glass.glass-control {
  box-shadow: inset 0 1px 0 var(--glassHighlight), inset 0 0 0 1px var(--glassRim), var(--glassShadowControl);
}
.glass.glass-clear, .glass.glass-flat {
  box-shadow: inset 0 1px 0 var(--glassHighlight), inset 0 0 0 1px var(--glassRim);
}
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .glass {
    background-image: linear-gradient(135deg, var(--glassSheen) 0%, transparent 45%);
    -webkit-backdrop-filter: var(--glassFilter);
    backdrop-filter: var(--glassFilter);
  }
  .glass.glass-regular { background-color: var(--glassTintRegular); }
  .glass.glass-prominent {
    background-color: var(--glassTintProminent);
    -webkit-backdrop-filter: var(--glassFilterProminent);
    backdrop-filter: var(--glassFilterProminent);
  }
  .glass.glass-clear {
    background-color: var(--glassTintClear);
    -webkit-backdrop-filter: var(--glassFilterClear);
    backdrop-filter: var(--glassFilterClear);
  }
  .glass.glass-interactive:active { background-color: var(--glassTintProminent); }
}
@media (prefers-contrast: more) {
  .glass.glass.glass { background-color: var(--glassTintContrast); }
}
@media (prefers-reduced-transparency: reduce) {
  .glass.glass.glass {
    background-color: var(--glassSolid);
    background-image: none;
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
}
`;

/** The glass material, web: backdrop blur over a theme tint. No layout opinions. */
export const GlassSurface = ({
  appearance = "auto",
  prominence: explicit,
  interactive = false,
  flat = false,
  shadow = "surface",
  className,
  ...props
}: GlassSurfaceProps) => {
  const prominence = useGlassProminence("regular");
  return <GlassTheme appearance={appearance} data-sentry-element="GlassTheme" data-sentry-component="GlassSurface" data-sentry-source-file="GlassSurface.tsx">
      {/* href + precedence let React hoist and dedupe this to one copy per document. */}
      <style href="glass-surface" precedence="default">
        {glassCss}
      </style>
      <YStack className={["glass", `glass-${explicit ?? prominence}`, interactive ? "glass-interactive" : null, flat ? "glass-flat" : null, shadow === "control" ? "glass-control" : null, className].filter(Boolean).join(" ")} {...props} data-sentry-element="YStack" data-sentry-source-file="GlassSurface.tsx" />
    </GlassTheme>;
};